import { dataFrom } from '@zyno-io/openapi-client-codegen';
import { ref } from 'vue';

import { app } from '@/generated/proto/softphone';
import { GlobalState } from '@/global';
import { TalkSoftphoneApi } from '@/openapi-clients-generated/talk';
import { Logger } from '@/shared/services/logger';
import { SrpcClient } from '@/shared/services/srpc';

export type ISoftphoneSrpcClient = SrpcClient<app.zyno.talk.softphone.CMessageStream, app.zyno.talk.softphone.SMessageStream>;

const log = Logger.createScoped('SoftphoneSrpcManager');

class SoftphoneSrpcManager {
    private client: ISoftphoneSrpcClient | null = null;
    /** Increments for every successfully established WebSocket, including reconnects. */
    private connectionGeneration = 0;
    private consumers = new Set<string>();
    private messageHandlers = new Map<string, (data: unknown) => Promise<unknown>>();

    /**
     * Register a consumer and get the shared sRPC client
     * Creates the connection if this is the first consumer
     */
    async registerConsumer(consumerId: string): Promise<ISoftphoneSrpcClient> {
        log.info('Registering consumer:', consumerId);
        this.consumers.add(consumerId);

        if (!this.client) {
            this.createClient();
        }

        if (!this.client!.isConnected) {
            await new Promise<void>((resolve, reject) => {
                this.client!.addEventListener(
                    'connected',
                    () => {
                        resolve();
                    },
                    { once: true }
                );

                this.client!.addEventListener(
                    'error',
                    event => {
                        reject((event as CustomEvent).detail);
                    },
                    { once: true }
                );
            });
        }

        return this.client!;
    }

    /**
     * Unregister a consumer
     * Tears down the connection if this was the last consumer
     */
    unregisterConsumer(consumerId: string): void {
        log.info('Unregistering consumer:', consumerId);
        this.consumers.delete(consumerId);

        if (this.consumers.size === 0 && this.client) {
            log.info('No more consumers, disconnecting sRPC client');
            this.client.disconnect();
            this.client = null;
            this.messageHandlers.clear();
        }
    }

    /**
     * Register a message handler for a specific consumer
     * Multiple consumers can register handlers for the same message type
     */
    registerMessageHandler<T extends keyof app.zyno.talk.softphone.SMessageStream>(
        consumerId: string,
        messageType: T,
        // oxlint-disable-next-line no-explicit-any
        handler: (data: any) => Promise<any>
    ): void {
        const key = `${consumerId}:${String(messageType)}`;
        this.messageHandlers.set(key, handler);
    }

    /**
     * Unregister all message handlers for a consumer
     */
    unregisterMessageHandlers(consumerId: string): void {
        for (const key of this.messageHandlers.keys()) {
            if (key.startsWith(`${consumerId}:`)) {
                this.messageHandlers.delete(key);
            }
        }
    }

    private async dispatchRegisteredHandlers(messageType: string, data: unknown, options: { failOnError?: boolean } = {}): Promise<unknown[]> {
        const results = [];
        for (const [key, handler] of this.messageHandlers.entries()) {
            if (!key.endsWith(`:${messageType}`)) continue;
            try {
                results.push(await handler(data));
            } catch (err) {
                log.error('Error in message handler:', err, { key });
                if (options.failOnError) throw err;
            }
        }
        return results;
    }

    private createClient(): void {
        log.info('Creating sRPC client');

        this.client = new SrpcClient<app.zyno.talk.softphone.CMessageStream, app.zyno.talk.softphone.SMessageStream>({
            uri: import.meta.env.VITE_APP_TALK_SOFTPHONE_WSS_URI!,
            clientOutput: app.zyno.talk.softphone.CMessageStream,
            serverInput: app.zyno.talk.softphone.SMessageStream,
            debug: true,
            metadata: async () => {
                const user = GlobalState.user.value;
                if (!user) {
                    return null;
                }

                // Request a v2 (device-bound) token: the backend derives deviceType+deviceId from the
                // authenticated identity, signs them, and returns the cid.
                const { token, cid } = dataFrom(await TalkSoftphoneApi.postSoftphoneGenerateConnectionToken({ query: { v2: true } }));

                // The bridge keys each WS connection by the full cid and supersedes duplicates, but a
                // browser is multi-window and every window shares one signed device identity. Append a
                // per-connection nonce so each window is a distinct connection; the bridge strips this
                // `~{nonce}` before verifying the signed device id, so windows still coalesce as one device.
                const nonce = Math.random().toString(36).substring(2, 15);

                return {
                    // v2 cid is `v2/{deviceType}/{deviceId}`; suffix the per-window nonce. Fall back to the
                    // legacy v1 cid (already per-connection unique) if the backend didn't issue one.
                    clientId: cid ? `${cid}~${nonce}` : `spa/${GlobalState.browserId}/${nonce}`,
                    appVersion: import.meta.env.VITE_APP_VERSION ?? '0.0.0',
                    accountId: GlobalState.user.value!.id,
                    token,
                    protoVersion: '5'
                };
            }
        });

        // Register event listeners
        this.client.addEventListener('connecting', () => {
            log.info('sRPC connecting...');
        });

        this.client.addEventListener('connected', () => {
            this.connectionGeneration++;
            log.info('sRPC connected');
        });

        this.client.addEventListener('disconnected', () => {
            log.info('sRPC disconnected');
        });

        // Register global message handler that dispatches to consumer-specific handlers
        this.client.registerMessageHandler('sUpdateCallStatusRequest', 'sUpdateCallStatusResponse', async data => {
            log.info('Call status update received:', data);

            // Call all registered handlers for this message type.
            await this.dispatchRegisteredHandlers('sUpdateCallStatusRequest', data);

            // Return empty response (handlers manage their own state)
            return {};
        });

        this.client.registerMessageHandler('sRenegotiateCallRequest', 'sRenegotiateCallResponse', async data => {
            log.info('Renegotiate call request received:', data);
            const results = await this.dispatchRegisteredHandlers('sRenegotiateCallRequest', data, { failOnError: true });
            if (results.length !== 1) {
                throw new Error(`Expected exactly one sRenegotiateCall handler, got ${results.length}`);
            }
            const response = results[0] as app.zyno.talk.softphone.ISRenegotiateCallResponse | undefined;
            if (!response?.answerSdp) {
                throw new Error('sRenegotiateCall handler returned no answerSdp');
            }
            return response;
        });

        this.client.registerMessageHandler('sRenegotiateCompleteRequest', 'sRenegotiateCompleteResponse', async data => {
            log.info('Renegotiate completion received:', data);
            const results = await this.dispatchRegisteredHandlers('sRenegotiateCompleteRequest', data, { failOnError: true });
            if (results.length !== 1) {
                throw new Error(`Expected exactly one sRenegotiateComplete handler, got ${results.length}`);
            }
            return results[0] as app.zyno.talk.softphone.ISRenegotiateCompleteResponse;
        });

        this.client.registerMessageHandler('sMediaFailureHintRequest', 'sMediaFailureHintResponse', async data => {
            log.info('Media failure hint received:', data);
            await this.dispatchRegisteredHandlers('sMediaFailureHintRequest', data);
            return {};
        });

        // Actually start the connection
        this.client.connect();
    }

    /**
     * Get the current client (if any)
     * Used for checking connection state
     */
    getClient(): ISoftphoneSrpcClient | null {
        return this.client;
    }

    /** A transfer replay capability proof is scoped to this exact live WebSocket. */
    getConnectedGeneration(): number | undefined {
        return this.client?.isConnected ? this.connectionGeneration : undefined;
    }

    /**
     * Get connection state
     */
    get isConnected(): boolean {
        return this.client?.isConnected ?? false;
    }
}

// Export singleton instance
export const softphoneSrpcManager = new SoftphoneSrpcManager();

// Export reactive connection state for UI
export const softphoneSrpcConnected = ref(false);

// Update reactive state when connection changes
setInterval(() => {
    softphoneSrpcConnected.value = softphoneSrpcManager.isConnected;
}, 1000);
